import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { ArrowLink } from "@/components/blocks/ArrowLink";
import { NightDecor } from "@/components/blocks/decor/Decor";
import { ExternalLinkButton } from "@/components/blocks/ExternalLinkButton";
import { AiFlowScene } from "@/components/blocks/scenes/AiFlowScene";
import { BlueprintScene } from "@/components/blocks/scenes/BlueprintScene";
import { BusinessAppCost } from "@/components/blocks/scenes/CostCompare";
import { FieldReportScene } from "@/components/blocks/scenes/FieldReportScene";
import { PillarGlyph } from "@/components/blocks/scenes/PillarGlyphs";
import { PipelineScene } from "@/components/blocks/scenes/PipelineScene";
import { QuestMap, QuestScene } from "@/components/blocks/scenes/QuestScene";
import { Checklist } from "@/components/blocks/sections/Checklist";
import { Facts } from "@/components/blocks/sections/Facts";
import { NumberedPoints } from "@/components/blocks/sections/NumberedPoints";
import { Section } from "@/components/blocks/sections/Section";
import {
	displayH2,
	Overline,
	SectionHeading,
} from "@/components/blocks/sections/SectionHeading";
import { StickyStory } from "@/components/blocks/sections/StickyStory";
import { Reveal, RevealGroup, RevealItem } from "@/components/motion/motion";
import { Button } from "@/components/ui/button";
import { QUESTOVERY_URL } from "@/lib/constants";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

export function ServicesOverview({ id }: { id: string }) {
	const { dict } = useI18n();
	const services = dict.home.services;
	const panels = [
		{
			key: "businessApps",
			copy: services.items.businessApps,
			to: "/{-$locale}/services/applications-metier",
			scene: <FieldReportScene />,
		},
		{
			key: "expoMigration",
			copy: services.items.expoMigration,
			to: "/{-$locale}/services/migration-react-native-expo",
			scene: <PipelineScene className="max-w-xl" />,
		},
	] as const;

	return (
		<Section id={id}>
			<SectionHeading
				index="01"
				label={services.label}
				title={services.title}
				description={services.description}
			/>
			<div className="mt-16 space-y-6 md:mt-20">
				{panels.map(({ key, copy, to, scene }, index) => (
					<Reveal
						key={key}
						as="section"
						className="grid overflow-hidden rounded-[2rem] border bg-card lg:grid-cols-2"
					>
						<div
							className={cn(
								"flex flex-col gap-6 p-6 sm:p-8 md:p-12",
								index % 2 === 1 && "lg:order-2",
							)}
						>
							<Overline>{copy.eyebrow}</Overline>
							<h3 className="text-[clamp(1.5rem,2.5vw,2.1rem)] leading-[1.1] font-semibold tracking-[-0.05em] text-balance">
								{copy.title}
							</h3>
							<p className="text-lg text-pretty text-muted-foreground">
								{copy.description}
							</p>
							<Checklist items={Object.values(copy.points)} />
							<p className="border-t pt-5 text-sm text-pretty text-muted-foreground">
								{copy.audience}
							</p>
							<div className="mt-auto pt-2">
								<ArrowLink to={to}>{copy.link}</ArrowLink>
							</div>
						</div>
						<div className="dot-grid flex items-center justify-center bg-muted/70 px-4 py-10 sm:px-6 md:px-12 md:py-12">
							{scene}
						</div>
					</Reveal>
				))}
			</div>
		</Section>
	);
}

export function AiDevelopment({ id }: { id: string }) {
	const { dict } = useI18n();
	const ai = dict.home.ai;
	const economics = dict.economics;

	return (
		<Section id={id} tone="stone">
			<SectionHeading
				index="02"
				label={ai.label}
				title={ai.title}
				description={ai.description}
			/>
			<div className="mt-16 grid items-center gap-8 md:mt-20 lg:grid-cols-2 lg:gap-12">
				<Reveal>
					<BusinessAppCost />
				</Reveal>
				<Reveal delay={0.15}>
					<AiFlowScene />
				</Reveal>
			</div>
			<NumberedPoints
				className="mt-16 md:mt-20"
				title={economics.methodTitle}
				items={economics.method}
				columns
			/>
			<Reveal className="mt-12 grid gap-6 rounded-3xl border border-dashed p-7 md:grid-cols-[minmax(0,1fr)_auto] md:items-end md:gap-10 md:p-10">
				<div>
					<h3 className="text-xl font-semibold tracking-tight md:text-2xl">
						{ai.saas.title}
					</h3>
					<p className="mt-3 max-w-3xl text-pretty text-muted-foreground">
						{ai.saas.description}
					</p>
				</div>
				<ArrowLink
					to="/{-$locale}/services/applications-metier"
					className="justify-self-start"
				>
					{ai.saas.link}
				</ArrowLink>
			</Reveal>
		</Section>
	);
}

export function FeaturedWork({ id }: { id: string }) {
	const { dict } = useI18n();
	const work = dict.home.work;

	return (
		<Section id={id} tone="night" decor={<NightDecor />}>
			<div className="grid items-center gap-16 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)] lg:gap-20">
				<Reveal className="space-y-8">
					<Overline index="03">{work.label}</Overline>
					<h2 className={displayH2}>{work.title}</h2>
					<p className="max-w-xl text-lg text-pretty text-muted-foreground">
						{work.description}
					</p>
					<Facts items={work.facts} className="sm:grid-cols-1 xl:grid-cols-3" />
					<ArrowLink to="/{-$locale}/realisations/questovery">
						{work.link}
					</ArrowLink>
				</Reveal>
				<Reveal delay={0.15}>
					<QuestScene />
				</Reveal>
			</div>
		</Section>
	);
}

export function Collaboration({ id }: { id: string }) {
	const { dict } = useI18n();
	const process = dict.home.process;

	return (
		<Section id={id}>
			<SectionHeading
				index="04"
				label={process.label}
				title={process.title}
				description={process.description}
			/>
			<StickyStory
				className="mt-16 md:mt-24"
				steps={Object.entries(process.steps).map(([key, step]) => ({
					key,
					title: step.title,
					description: step.description,
					details: [{ label: dict.common.yourRole, text: step.client }],
				}))}
				renderVisual={(index) => <BlueprintScene stage={index} />}
			/>
			<Reveal className="mt-16 max-w-3xl border-t pt-6 text-sm text-pretty text-muted-foreground lg:ml-[calc(45%+2.5rem)]">
				{process.note}
			</Reveal>
		</Section>
	);
}

const PILLARS = ["design", "modernize", "publish"] as const;

export function Company({ id }: { id: string }) {
	const { dict } = useI18n();
	const company = dict.home.company;

	return (
		<Section id={id} tone="stone">
			<SectionHeading
				index="05"
				label={company.label}
				title={company.title}
				description={company.description}
			/>
			<RevealGroup
				as="ul"
				className="mt-16 grid border-t md:mt-20 md:grid-cols-3 md:divide-x md:divide-border"
			>
				{PILLARS.map((key, index) => (
					<RevealItem
						as="li"
						key={key}
						className="border-b py-10 md:border-b-0 md:px-10 md:first:pl-0 md:last:pr-0"
					>
						<PillarGlyph kind={key} />
						<p className="mt-8 font-mono text-xs text-signal-ink">
							{String(index + 1).padStart(2, "0")}
						</p>
						<h3 className="mt-2 text-2xl font-semibold tracking-[-0.03em]">
							{company.pillars[key].title}
						</h3>
						<p className="mt-3 text-pretty text-muted-foreground">
							{company.pillars[key].description}
						</p>
					</RevealItem>
				))}
			</RevealGroup>
			<div className="mt-12">
				<ArrowLink to="/{-$locale}/a-propos">{company.link}</ArrowLink>
			</div>
		</Section>
	);
}

export function ProductTeaser({ id }: { id: string }) {
	const { dict, localeParams } = useI18n();
	const products = dict.home.products;

	return (
		<Section id={id}>
			<Reveal className="relative grid gap-10 overflow-hidden rounded-[2rem] border p-8 md:p-14 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
				<div className="space-y-6">
					<Overline index="06">{products.label}</Overline>
					<h2 className="text-[clamp(2.6rem,7vw,6rem)] leading-[0.95] font-semibold tracking-[-0.07em]">
						{products.title}
						<ArrowUpRight
							aria-hidden="true"
							className="ml-2 inline size-[0.5em] align-top text-signal"
							strokeWidth={1.5}
						/>
					</h2>
					<p className="max-w-xl text-lg text-pretty text-muted-foreground">
						{products.description}
					</p>
					<div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
						<ExternalLinkButton
							href={QUESTOVERY_URL}
							cta="questovery"
							placement="home_products"
						>
							{products.link}
						</ExternalLinkButton>
						<Button
							variant="outline"
							size="lg"
							className="h-auto min-h-11 rounded-lg px-6 font-mono text-[13px]"
							asChild
						>
							<Link to="/{-$locale}/produits" params={localeParams}>
								{products.secondaryLink}
							</Link>
						</Button>
					</div>
				</div>
				<div
					aria-hidden="true"
					className="paper relative mx-auto hidden size-72 overflow-hidden rounded-full border shadow-sm md:block lg:size-80"
				>
					<QuestMap />
				</div>
			</Reveal>
		</Section>
	);
}
